import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../store/authStore';
import { clearUserScopedStorage } from '../../lib/sessionCleanup';

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const { setUser, setLoading } = useAuthStore();

  useEffect(() => {
    // Check active session on mount
    const initializeAuth = async () => {
      try {
        setLoading(true);
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error getting session:', error);
          // Try to refresh if getSession fails
          const { data: refreshData } = await supabase.auth.refreshSession();
          setUser(refreshData?.session?.user ?? null);
        } else {
          setUser(session?.user ?? null);
        }
      } catch (error) {
        console.error('Error checking session:', error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // Listen for auth changes — only act on definitive events.
    // TOKEN_REFRESHED can fire with a null session during a transient network
    // failure; blindly calling setUser(null) there logs the user out incorrectly.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.warn('[Auth]', event, session?.user?.id ?? 'no user');

      if (event === 'SIGNED_IN' || event === 'USER_UPDATED' || event === 'INITIAL_SESSION') {
        setUser(session?.user ?? null);
      } else if (event === 'TOKEN_REFRESHED') {
        // Only update if we actually got a valid user back
        if (session?.user) {
          setUser(session.user);
        }
        // If session is null here it's a transient refresh failure — do nothing,
        // Supabase will retry automatically.
      } else if (event === 'SIGNED_OUT') {
        clearUserScopedStorage();
        setUser(null);
      }
    });

    return () => subscription.unsubscribe();
  }, [setUser, setLoading]);

  return <>{children}</>;
};
